<template>
    <div ref="root" class="scrollable-text-card">
        <button ref="catcherFocus" class="scrollable-text-card__catcherFocus" @focus="onFocusCatcher" @blur="onBlurCatcher" />
        <div
            ref="wrap"
            class="scrollable-text-card__wrap"
            :style="{'--wrapTopPosition': `${wrapTopPosition}px` }"
        >
            <div v-if="!headerSlotIsEmpty" class="scrollable-text-card__title h4">
                <slot name="header" />
            </div>
            <div class="scrollable-text-card__contents">
                <slot name="body" />
            </div>
        </div>
        <div
            class="scrollable-text-card__scroller"
            :style="scrollerStyles"
        />
    </div>
</template>

<script setup lang="ts">
    import { ref, type Ref, onBeforeUnmount, computed, useSlots, type StyleValue } from "vue";
    import type { ExposeInterface } from "./types";
     
    const { $navigator } = useNuxtApp();
    const wrapTopPosition: Ref<number> = ref(0);
    const scrollerHeight: Ref<number> = ref(0);
    const scrollerTopPosition: Ref<number> = ref(0);
    const isRegKeyListeners: Ref<boolean> = ref(false);
    const root: Ref<HTMLDivElement | null> = ref(null);
    const wrap: Ref<HTMLDivElement | null> = ref(null);
    const catcherFocus: Ref<HTMLButtonElement | null> = ref(null);
    const slots = useSlots()


    const rootHeight = computed((): number => root.value ? root.value.clientHeight : 0);
    const scrollerStyles = computed((): StyleValue => {
        return {
            top: `${scrollerTopPosition.value}%`,
            height: `${scrollerHeight.value}%`,
        };
    });
    const headerSlotIsEmpty = computed((): boolean => {
        return !slots["header"];
    });


    const scrollUp = () => {
        if (wrapTopPosition.value === 0) {
            return;
        }
        const newPosition = wrapTopPosition.value + rootHeight.value - (rootHeight.value / 2);
        if (newPosition > 0) {
            wrapTopPosition.value = 0;
            return;
        } else {
            wrapTopPosition.value = newPosition;
        }
        recountScroller();
    };
    const scrollDown = () => {
        if (wrap.value === null || root.value === null) {
            return;
        };

        const { bottom: wrapBottom, height: wrapHeight } = wrap.value.getBoundingClientRect();
        const { bottom: rootBottom } = root.value.getBoundingClientRect();
        if (wrapBottom <= rootBottom) {
            return;
        }
        if (wrapBottom - rootHeight.value < rootBottom) {
            wrapTopPosition.value = 0 - wrapHeight + rootHeight.value;
        } else {
            wrapTopPosition.value -= (rootHeight.value - (rootHeight.value / 2));
        }
        recountScroller();
    };
    const recountScroller = () => {
        if (wrap.value === null || root.value === null) {
            return;
        };

        const { height: wrapHeight } = wrap.value.getBoundingClientRect();
        const { height: rHeight } = root.value.getBoundingClientRect();
        scrollerHeight.value = Math.round((rHeight / wrapHeight) * 100);
        scrollerTopPosition.value = Math.abs(
            Math.round((wrapTopPosition.value / wrapHeight) * 100)
        );
    };
    const onFocusCatcher = () => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:up", scrollUp);
            $navigator.on("keypress:down", scrollDown);
            isRegKeyListeners.value = true;
        }
    };
    const onBlurCatcher = () => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:up", scrollUp);
            $navigator.off("keypress:down", scrollDown);
            isRegKeyListeners.value = false;
        }
    };
    const focusToCatcher = () => {
        catcherFocus.value?.focus();
    };
    
    onBeforeUnmount(() => {
        onBlurCatcher();
    });

    defineExpose<ExposeInterface>({
        focusToCatcher
    });
    
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
